<template>
  <k-modal v-if="programmeId && programmeName" :show="show" @close="$emit('close')">
    <template #header>
      <h3>Review OTJs for <b>{{ learnerName }}</b> in <b>{{ programmeName }}</b></h3>
    </template>
    <template #body>
      <div class="button-container">
        <k-date-filters
          @filter-window-changed="onFilterWindowChanged"
        />
      </div>
      <k-table v-if="filteredRows.length > 0"
        class="pending-otj-table"
        :headers="kableHeaders"
        :rows="filteredRows"
        :hideable="false"
        :panel="false"
        :max="10"
        @clicked="handleTableAction">
      </k-table>

      <empty-placeholder v-else
        title="No O.T.J.s found for this filter"
        info="No O.T.J.s were found for this filter. If you feel something is missing please contact us."
      ></empty-placeholder>

      <add-comments-otj-modal
        v-if="currentOtjForComments"
        :showModal="showCommentsModal"
        :otjData="currentOtjForComments"
        :programmeId="programmeId"
        :programmeMemberId="programmeMemberId"
        :action="pendingAction"
        @update-status="setOtjStatus"
        @close="closeCommentsModal"
      ></add-comments-otj-modal>
    </template>
    <template #footer>
      <button class="btn btn-primary" @click="$emit('close')">Close</button>
    </template>
  </k-modal>
</template>

<style scoped>
h3 b {
  font-family: hk-grotesk-medium, sans-serif;
}
</style>

<style>
.status-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  display: inline-block;
}

.status-badge.pending {
  background-color: var(--kate-primary);
  color: var(--kate-type-dark);
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-buttons button {
  padding: 6px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-buttons button i {
  font-size: 1rem;
}

.action-buttons button.btn-success {
  background-color: var(--kate-success);
  color: white;
}

.action-buttons button.btn-success:hover {
  background-color: var(--kate-success-dark);
}

.status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.status-icon.success {
  background-color: var(--kate-success);
  color: white;
}

.status-icon.danger {
  background-color: var(--kate-danger);
  color: white;
}

.status-icon i {
  font-size: 0.9rem;
}

.pending-otj-table.k-table-container th.k-table-header-refer,
.pending-otj-table.k-table-container th.k-table-header-approve {
  min-width: 85px;
}

.pending-otj-table th.k-table-header-refer .k-table-header-content,
.pending-otj-table th.k-table-header-approve .k-table-header-content {
  justify-content: center;
}

.pending-otj-table .k-table-key-refer,
.pending-otj-table .k-table-key-approve {
  text-align: center;
}

.pending-otj-table .k-table-key-date {
  white-space: nowrap;
}

.pending-otj-table.k-table-container th {
  min-width: unset;
}

</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KDateFilters from '@base-components/buttons/k-date-filters.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import KModal from '@base-components/k-modal.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import { sortObjectArray } from '@utils/sort-by-object-property';
import AddCommentsOtjModal from './add-comments-otj-modal.vue';

export default {
  components: {
    KTable,
    KModal,
    KDateFilters,
    EmptyPlaceholder,
    AddCommentsOtjModal,
  },

  mixins: [TimeMixin],

  props: {
    otjs: {
      type: Array,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
    programmeId: {
      type: [String, Number],
      required: true,
    },
    learnerId: {
      type: [String, Number],
    },
    learnerName: {
      type: String,
      required: true,
    },
    programmeMemberId: {
      type: [String, Number],
    },
    programmeName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      showCommentsModal: false,
      otjId: null,
      pendingAction: null, // true for approving and false for referring
      filterWindowStart: null,
      filterWindowEnd: null,
    };
  },

  computed: {
    kableHeaders() {
      const headers = {
        date: {
          name: 'Date',
          type: 'datetime',
          sortable: true,
          filterable: true,
          filterBy: date => this.parseTimestamp(date, undefined, 'dd/MM/yyyy'),
          options: {
            datetimeFormat: 'dd/MM/yyyy',
          },
        },
        start_time: {
          name: 'Start',
          type: 'datetime',
          options: {
            datetimeFormat: 'h:mm a',
          },
        },
        end_time: {
          name: 'End',
          type: 'datetime',
          options: {
            datetimeFormat: 'h:mm a',
          },
        },
        time_spent: {
          name: 'Duration',
          type: 'string',
          sortable: true,
          filterable: true,
          displayAs: timeSpent => this.formatTimeSpent(timeSpent),
          filterBy: timeSpent => this.formatTimeSpent(timeSpent),
        },
        status: {
          name: 'Status',
          type: 'pill',
          sortable: true,
          options: {
            type: status => {
              const statusMap = {
                approved: 'success',
                referred: 'warning',
                rejected: 'danger',
                pending: 'primary',
                draft: 'neutral',
              };
              return statusMap[status.toLowerCase()] || 'neutral';
            },
          },
        },
        learning_activity: {
          name: 'Activity',
          type: 'string',
        },
        learning_description: {
          name: 'Description',
          type: 'string',
          filterable: true,
        },
        competencies: {
          name: 'Competencies',
          type: 'tags',
          filterable: true,
        },
        approve: {
          name: 'Approve',
          type: 'button',
          options: {
            preset: 'approve',
            title: 'Approve',
            ariaLabel: 'Approve',
          },
        },
        refer: {
          name: 'Refer',
          type: 'button',
          options: {
            preset: 'refer',
            title: 'Refer',
            ariaLabel: 'Refer',
          },
        },
      };
      if (this.canDeleteOtj) {
        headers.delete = {
          name: 'Delete',
          type: 'button',
          options: {
            preset: 'delete',
            title: 'Delete',
            ariaLabel: 'Delete',
          },
        };
      }
      return headers;
    },

    currentOtjForComments() {
      return this.otjs?.find(o => o.id === this.otjId);
    },

    kableRows() {
      if (!this.otjs) {
        return [];
      }
      return sortObjectArray(this.otjs, 'created_at', true).map(otj => {
        const row = {
          id: otj.id,
          date: otj.start_time,
          start_time: otj.start_time,
          end_time: otj.end_time,
          time_spent: otj.time_spent,
          status: otj.status === 'rejected' ? 'Referred' : otj.status,
          learning_activity: otj.category,
          learning_description: otj.learning_description,
          competencies: {
            tags: otj.competencies ? otj.competencies.map(c => c.label) : [],
          },
        };
        return row;
      });
    },

    canDeleteOtj() {
      return this.$permissions.hasPermission('manage_user_otjs');
    },

    filteredRows() {
      if (!this.filterWindowStart || !this.filterWindowEnd) {
        return this.kableRows;
      }

      return this.kableRows.filter(row => {
        const date = this.getDate(row.date);
        return date >= this.filterWindowStart && date <= this.filterWindowEnd;
      });
    },
  },

  methods: {
    setOtjStatus(otjId, value) {
      const otj = this.otjs?.find(o => o.id === otjId);
      if (!otj || !this.programmeId) {
        return;
      }
      const jsonPayload = {
        status: value ? 'approved' : 'rejected',
        programme_competency_ids: otj.competencies ? otj.competencies.map(c => c.id) : [],
        category: otj.category,
        learning_description: otj.learning_description,
        declared_in_working_hours: otj.declared_in_working_hours,
        user_id: this.learnerId,
        programme_member_id: this.programmeMemberId,
        start_time: this.formatDate(otj.start_time),
        end_time: this.formatDate(otj.end_time),
      };
      this.$Loading.start();
      this.$logger.info('Setting OTJ status', { otjId }, true);
      this.$http.put(`/api/curriculum/admin/programmes/${this.programmeId}/otj-entries/${otjId}`, jsonPayload)
        .then(() => {
          this.$logger.info('OTJ status set', { otjId });
          this.closeOtjModalIfLastRow();
          this.$emit('update');
        })
        .catch(err => {
          this.$logger.error('Error setting OTJ status', { otjId }, err);
          this.$ktoast.showError(err, true);
        }).then(() => {
          this.$Loading.finish();
        });
    },

    deleteDashboardOtj(otjId) {
      this.$logger.info('Deleting learner OTJ', { id: otjId });
      this.$Loading.start();
      this.$http.delete(`/api/curriculum/admin/programmes/${this.programmeId}/otj-entries/${otjId}/${this.learnerId}`)
        .then(() => {
          this.$logger.info('Learner OTJ deleted', { id: otjId });
          this.$ktoast.success('Learner OTJ deleted');
          this.closeOtjModalIfLastRow();
          this.$emit('update');
        })
        .catch(err => {
          this.$logger.autowarn('Error deleting learner OTJ', { id: otjId }, err);
          this.$ktoast.warning('Error deleting learner OTJ');
        }).then(() => {
          this.$Loading.finish();
        });
    },

    handleTableAction(key, index, row, column) {
      if (column === 'approve') {
        this.openConfirmationModal(row.id, true);
      } else if (column === 'refer') {
        this.openConfirmationModal(row.id, false);
      } else if (column === 'delete') {
        this.confirmDeleteOtj(row.id);
      }
    },

    closeOtjModalIfLastRow() {
      if (this.otjs.length === 1) {
        this.$emit('close');
      }
    },

    confirmDeleteOtj(otjId) {
      this.$ktoast.confirmToast('Are you sure you want to delete this OTJ?', 'warning', () => {
        this.deleteDashboardOtj(otjId);
      });
    },

    openConfirmationModal(otjId, action) {
      const otj = this.otjs?.find(o => o.id === otjId);
      if (!otj) {
        return;
      }
      this.otjId = otjId;
      this.pendingAction = action;
      this.showCommentsModal = true;
    },

    closeCommentsModal() {
      this.showCommentsModal = false;
      this.otjId = null;
      this.pendingAction = null;
    },

    onFilterWindowChanged({ start, end }) {
      this.filterWindowStart = start;
      this.filterWindowEnd = end;
    },
  },
};
</script>
